import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { colors, radii, spacing } from '../theme/tokens';
import { Button, Icon } from '../components/ui';
import SubNav from '../components/ui/SubNav';
import useIsDesktop from '../hooks/useIsDesktop';
import { useToast } from '../components/Toast';
import { tasksApi } from '../lib/tasksApi';
import TaskList from '../components/tasks/TaskList';
import TaskAnalytics from '../components/tasks/TaskAnalytics';
import TaskDashboard from '../components/tasks/TaskDashboard';
import ProjectConfig from '../components/tasks/ProjectConfig';

const RESERVED = ['dashboard', 'analytics', 'configure', 'overview'];

// Task Tracking section (project "ProAuto Rubber"). Rubber-tier only.
// Departments are the first sub-menu; Analytics is an extra pill; the Configure
// Project button (top-right) opens department + task-type settings.
const TaskTracking = () => {
  const { canAccessTasks, isRubber } = useAuth();
  const navigate = useNavigate();
  const { tab } = useParams();
  const [searchParams] = useSearchParams();
  const expandTaskId = searchParams.get('task');
  const showToast = useToast();
  const isDesktop = useIsDesktop();

  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    try {
      const [boot, us] = await Promise.all([tasksApi.bootstrap(), tasksApi.users()]);
      setProject(boot.project);
      setDepartments(boot.departments || []);
      setTaskTypes(boot.taskTypes || []);
      setFieldDefs(boot.fieldDefs || []);
      setUsers(us || []);
    } catch (e) {
      showToast('Failed to load task workspace', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (!canAccessTasks) {
      navigate('/');
      return;
    }
    load();
  }, [canAccessTasks, navigate, load]);

  const activeDept = departments.find((d) => d.slug === tab);

  // Send the index/unknown tab to the dashboard.
  useEffect(() => {
    if (loading) return;
    if (RESERVED.includes(tab)) return;
    if (activeDept) return;
    navigate('/task-tracking/dashboard', { replace: true });
  }, [loading, tab, activeDept, navigate]);

  // Configuration (departments, task types, access lists) is rubber-only.
  useEffect(() => {
    if (loading) return;
    if (tab === 'configure' && !isRubber) {
      navigate('/task-tracking/dashboard', { replace: true });
    }
  }, [loading, tab, isRubber, navigate]);

  if (!canAccessTasks) return null;
  if (loading) return <div style={styles.loading}>Loading task workspace…</div>;

  const isConfigure = tab === 'configure' && isRubber;

  const openTask = (slug, taskId) => navigate(`/task-tracking/${slug}?task=${taskId}`);

  const subItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: 'dashboard',
      active: tab === 'dashboard',
      onClick: () => navigate('/task-tracking/dashboard')
    },
    ...departments.map((d) => ({
      key: d.slug,
      label: d.name,
      // Guest departments (tagged in, not a member) get a tag icon.
      icon: d.guest ? 'sell' : (d.icon || 'folder'),
      active: tab === d.slug,
      onClick: () => navigate(`/task-tracking/${d.slug}`)
    })),
    {
      key: 'analytics',
      label: 'Analytics',
      icon: 'bar_chart',
      active: tab === 'analytics',
      onClick: () => navigate('/task-tracking/analytics')
    }
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.titleWrap}>
          <Icon name="checklist" size={26} color={colors.parGreenBright} />
          <h1 style={styles.title}>{project?.name || 'ProAuto Rubber'}</h1>
        </div>
        {isRubber && (
          <Button
            variant={isConfigure ? 'primary' : 'ghost'}
            size="sm"
            icon="settings"
            onClick={() =>
              navigate(isConfigure ? '/task-tracking/dashboard' : '/task-tracking/configure')
            }
          >
            {isConfigure ? 'Done' : 'Configure Project'}
          </Button>
        )}
      </div>

      {!isConfigure && (isDesktop ? (
        <SubNav align="start" items={subItems} />
      ) : (
        <select
          style={styles.deptSelect}
          value={tab === 'analytics' ? 'analytics' : (tab === 'dashboard' ? 'dashboard' : (activeDept?.slug || 'dashboard'))}
          onChange={(e) => navigate(`/task-tracking/${e.target.value}`)}
        >
          <option value="dashboard">Dashboard</option>
          <optgroup label="Departments">
            {departments.filter((d) => !d.guest).map((d) => (
              <option key={d.slug} value={d.slug}>{d.name}</option>
            ))}
          </optgroup>
          {departments.some((d) => d.guest) && (
            <optgroup label="Tagged in">
              {departments.filter((d) => d.guest).map((d) => (
                <option key={d.slug} value={d.slug}>{d.name}</option>
              ))}
            </optgroup>
          )}
          <option value="analytics">Analytics</option>
        </select>
      ))}

      <div style={{ ...styles.content, ...(isDesktop ? null : styles.contentMobile) }}>
        {isConfigure ? (
          <ProjectConfig departments={departments} taskTypes={taskTypes} fieldDefs={fieldDefs} users={users} onChange={load} />
        ) : tab === 'analytics' ? (
          <TaskAnalytics departments={departments.filter((d) => !d.guest)} />
        ) : tab === 'dashboard' ? (
          <TaskDashboard onOpenTask={openTask} />
        ) : activeDept ? (
          <TaskList department={activeDept} taskTypes={taskTypes} users={users} expandTaskId={expandTaskId} />
        ) : (
          <div style={styles.empty}>
            <Icon name="folder_open" size={42} color={colors.textFaint} />
            <p style={styles.emptyText}>
              No departments yet. Click <strong>Configure Project</strong> to add departments and task types.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: { padding: '4px 8px 24px', background: 'transparent' },
  deptSelect: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: radii.md,
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.text,
    fontSize: 15,
    fontWeight: 700,
    marginBottom: 12,
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  loading: { padding: '50px', textAlign: 'center', color: colors.textMuted },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    padding: '14px 8px 4px'
  },
  titleWrap: { display: 'flex', alignItems: 'center', gap: 10 },
  title: { margin: 0, fontSize: 'clamp(20px, 3vw, 28px)', fontWeight: 800, color: colors.text },
  content: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: radii.lg,
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    padding: spacing.lg,
    minHeight: '50vh'
  },
  contentMobile: { padding: spacing.sm, borderRadius: radii.md },
  empty: { textAlign: 'center', color: colors.textMuted, padding: '40px 16px' },
  emptyText: { marginTop: 12, fontSize: 15 }
};

export default TaskTracking;
